import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchData, HTTP_STATUS, intl, ROUTES } from 'dmsbasereact';
import {
    UserTypeEnum,
    includesPrescriber,
    includesDispenser,
    includesInstitutionResponsible,
    includesDistributorResponsible
} from '../../constants/UserType';
import { UserRole } from '../../constants/UserRole';
import Swal from "sweetalert2";

export const useCreateAccountForm = (searchParams) => {
    const navigate = useNavigate();
    const [status, setStatus] = useState("loading");
    const [crmValidationWarning, setCrmValidationWarning] = useState("");
    const [message, setMessage] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [cpfIsValid, setCpfIsValid] = useState(false);
    const [selectedInstitutions, setSelectedInstitutions] = useState([]);
    const [responsibleForInstitutions, setResponsibleForInstitutions] = useState([]);
    const [institutionNamesMap, setInstitutionNamesMap] = useState({}); // Map of pk -> name
    const [userTypes, setUserTypes] = useState([]);
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [crm, setCrm] = useState("");
    const [crmState, setCrmState] = useState("");
    const [isValidatingCrm, setIsValidatingCrm] = useState(false);
    const [isCrmValid, setIsCrmValid] = useState(false);
    const [crmValidationError, setCrmValidationError] = useState("");
    const [crf, setCrf] = useState("");
    const [crfState, setCrfState] = useState("");
    const [cpf, setCpf] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [zipCode, setZipCode] = useState("");
    const [city, setCity] = useState("");
    const [addressState, setAddressState] = useState("");
    const [address, setAddress] = useState("");
    const [addressNumber, setAddressNumber] = useState("");
    const [legalDocumentsAccepted, setLegalDocumentsAccepted] = useState(false);
    const [institutionLinkDeclaration, setInstitutionLinkDeclaration] = useState(false);
    const [legalDocument, setLegalDocument] = useState(null);
    const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
    const [loadingDocument, setLoadingDocument] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [countdown, setCountdown] = useState(5);
    const [createdUserName, setCreatedUserName] = useState("");
    const [institutionModalVisible, setInstitutionModalVisible] = useState(false);
    const [createdInstitutions, setCreatedInstitutions] = useState([]);
    const [showErrors, setShowErrors] = useState(false);
    const countdownRef = useRef(null);

    useEffect(() => {
        const token = searchParams.get("token");
        if (!token) {
            setStatus("error");
            setMessage("Token not provided.");
            return;
        }

        const verifyToken = async () => {
            try {
                const response = await fetchData(`/email/verify-token?token=${token}`, 'GET', false);
                if (response.status === HTTP_STATUS.OK_200) {
                    setStatus("success");
                    setMessage(response.data.message || "Email verified successfully!");
                    setEmail(response.data.email)
                } else {
                    setStatus("error");
                    setMessage(response.data.detail)
                }
            } catch (error) {
                setStatus("error");
                if (error.response?.data?.detail) {
                    setMessage(error.response.data.detail);
                } else {
                    setMessage("Failed to verify token.");
                }
            }
        };

        verifyToken();
    }, [searchParams]);

    useEffect(() => {
        const fetchLegalDocument = async () => {
            try {
                const response = await fetchData("/legal-document/public/terms_of_service", 'GET', false);
                if (response.status === HTTP_STATUS.OK_200) {
                    setLegalDocument(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch legal document:", error);
            }
        };

        fetchLegalDocument();
    }, []);

    const showResponsibleForField = useMemo(() => {
        return includesInstitutionResponsible(userTypes) || includesDistributorResponsible(userTypes);
    }, [userTypes]);

    const { isFormValid, validationErrors, fieldErrors } = useMemo(() => {
        const errors = [];

        // Basic field validations
        if (name.trim() === "") {
            errors.push({ field: "name", message: intl.get("NameRequired").d("Name is required") });
        }
        if (!cpfIsValid) {
            errors.push({ field: "cpf", message: intl.get("ValidCpfRequired").d("Valid CPF is required") });
        }
        if (phone.trim() === "") {
            errors.push({ field: "phone", message: intl.get("PhoneRequired").d("Phone is required") });
        }
        if (userTypes.length === 0) {
            errors.push({ field: "userTypes", message: intl.get("UserTypeRequired").d("Select at least one user type") });
        }
        // At least one institution is required (unless user created one or selected NO-INSTITUTION)
        if (!selectedInstitutions || selectedInstitutions.length === 0) {
            if (createdInstitutions.length === 0) {
                errors.push({ field: "selectedInstitutions", message: intl.get("InstitutionRequired").d("At least one institution is required") });
            }
        }

        // If institution responsible or distributor responsible type selected, at least one "responsible for" required
        if (includesInstitutionResponsible(userTypes) || includesDistributorResponsible(userTypes)) {
            if (!responsibleForInstitutions || responsibleForInstitutions.length === 0) {
                errors.push({
                    field: "responsibleForInstitutions",
                    message: intl.get("ResponsibleForRequired").d("Select at least one institution you are responsible for")
                });
            }
        }

        // Password validations
        if (password.trim() === "") {
            errors.push({ field: "password", message: intl.get("PasswordRequired").d("Password is required") });
        } else {
            const passwordValidations = {
                minLength: password.length >= 8,
                hasUppercase: /[A-Z]/.test(password),
                hasLowercase: /[a-z]/.test(password),
                hasNumber: /\d/.test(password),
                hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
            };
            if (!Object.values(passwordValidations).every(v => v)) {
                errors.push({ field: "password", message: intl.get("PasswordNotMeetRequirements").d("Password does not meet requirements") });
            }
        }
        if (confirmPassword.trim() === "") {
            errors.push({ field: "confirmPassword", message: intl.get("ConfirmPasswordRequired").d("Password confirmation is required") });
        } else if (password !== confirmPassword) {
            errors.push({ field: "confirmPassword", message: intl.get("PasswordsMustMatch").d("Passwords must match") });
        }

        // Type-specific validations for prescriber (doctor)
        if (includesPrescriber(userTypes)) {
            if (crm.trim() === "") {
                errors.push({ field: "crm", message: intl.get("CrmRequired").d("CRM is required") });
            }
            if (crmState === "") {
                errors.push({ field: "crmState", message: intl.get("CrmStateRequired").d("CRM state is required") });
            }
            if (!isCrmValid) {
                errors.push({ field: "crmValidation", message: intl.get("CrmValidationRequired").d("CRM validation is required") });
            }
            if (!institutionLinkDeclaration) {
                errors.push({ field: "institutionLinkDeclaration", message: intl.get("InstitutionLinkDeclarationRequired").d("Institution link declaration is required") });
            }
        }

        // Type-specific validations for dispenser (pharmacist)
        if (includesDispenser(userTypes)) {
            if (crf.trim() === "") {
                errors.push({ field: "crf", message: intl.get("CrfRequired").d("CRF is required") });
            }
            if (crfState === "") {
                errors.push({ field: "crfState", message: intl.get("CrfStateRequired").d("CRF state is required") });
            }
        }

        // Consent validation
        if (!legalDocumentsAccepted) {
            errors.push({ field: "legalDocumentsAccepted", message: intl.get("LegalDocumentsAcceptanceRequired").d("You must accept the Terms of Use and Privacy Policy to continue") });
        }

        // Address validations (optional for dispensers only, required for all other user types)
        const isDispenserOnly = includesDispenser(userTypes) && !includesPrescriber(userTypes) && !includesInstitutionResponsible(userTypes) && !includesDistributorResponsible(userTypes);

        if (!isDispenserOnly) {
            if (zipCode.trim() === "") {
                errors.push({ field: "zipCode", message: intl.get("ZipCodeRequired").d("Zip code is required") });
            }
            if (address.trim() === "") {
                errors.push({ field: "address", message: intl.get("AddressRequired").d("Address is required") });
            }
            if (addressNumber.trim() === "") {
                errors.push({ field: "addressNumber", message: intl.get("NumberRequired").d("Number is required") });
            }
            if (city.trim() === "") {
                errors.push({ field: "city", message: intl.get("CityRequired").d("City is required") });
            }
            if (addressState === "") {
                errors.push({ field: "addressState", message: intl.get("StateRequired").d("State is required") });
            }
        }

        // Convert errors array to field map
        const errorMap = errors.reduce((acc, err) => {
            acc[err.field] = err.message;
            return acc;
        }, {});

        // Only show field errors after user attempts to submit
        const visibleFieldErrors = showErrors ? errorMap : {};

        return {
            isFormValid: errors.length === 0,
            validationErrors: errors,
            fieldErrors: visibleFieldErrors,
        };
    }, [name, cpfIsValid, password, confirmPassword, phone, userTypes, selectedInstitutions, responsibleForInstitutions, crm, crmState, crf, crfState, legalDocumentsAccepted, institutionLinkDeclaration, isCrmValid, zipCode, address, addressNumber, city, addressState, createdInstitutions, showErrors]);

    const canValidateCrm = useMemo(() => {
        return name.trim() !== "" && crm.trim() !== "" && crmState !== "";
    }, [name, crm, crmState]);

    const handleCrmValidation = useCallback(async () => {
        if (isValidatingCrm || !canValidateCrm) return;

        setIsValidatingCrm(true);
        setCrmValidationError("");
        setCrmValidationWarning("");

        try {
            const response = await fetchData("/doctor/validate_crm", "POST", false, {
                doctor_name: name,
                crm: crm,
                crm_state: crmState
            });

            const data = response?.data?.data || response?.data || {};

            if (data.is_regular === true) {
                setIsCrmValid(true);
                setCrmValidationError("");

                if (!data.name_matches) {
                    setCrmValidationWarning(
                        intl.get("CrmNameDoesNotMatchWarning").d(
                            "To ensure the standardization of records, we recommend using the same name as registered in the CRM."
                        )
                    );
                }
            }
            else {
                setIsCrmValid(false);
                setCrmValidationError(intl.get("CrmNotRegular").d("CRM is not regular: ") + (data.cfm_data?.situacao_texto || ""));
            }
        } catch (error) {
            setIsCrmValid(false);
            if (error.response?.status === 404) {
                setCrmValidationError(intl.get("CouldNotFindDoctorRegistry").d("Could not find doctor registry in CFM. Please check the CRM number and state."));
            } else {
                setCrmValidationError(intl.get("CrmValidationError").d("Error validating CRM. Please try again."));
            }
        } finally {
            setIsValidatingCrm(false);
        }
    }, [name, crm, crmState, isValidatingCrm, canValidateCrm]);

    const handleOpenDocument = () => {
        setDocumentDialogOpen(true);
    };

    const handleCloseDocument = () => {
        setDocumentDialogOpen(false);
    };

    const startCountdown = useCallback(() => {
        setCountdown(5);
        let count = 5;
        countdownRef.current = setInterval(() => {
            count -= 1;
            if (count <= 0) {
                clearInterval(countdownRef.current);
                setCountdown(0);
                navigate(ROUTES.Login);
            } else {
                setCountdown(count);
            }
        }, 1000);
    }, [navigate]);

    useEffect(() => {
        return () => {
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        };
    }, []);

    // Auto-remove from responsibleFor if removed from main selection
    useEffect(() => {
        if (responsibleForInstitutions.length > 0) {
            const validResponsibleFor = responsibleForInstitutions.filter(
                instPk => selectedInstitutions.includes(instPk)
            );
            if (validResponsibleFor.length !== responsibleForInstitutions.length) {
                setResponsibleForInstitutions(validResponsibleFor);
            }
        }
    }, [selectedInstitutions, responsibleForInstitutions]);

    const handleSuccessModalClose = () => {
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
        }
        setSuccessModalOpen(false);
        navigate(ROUTES.Login);
    };

    const resetCrmValidation = useCallback(() => {
        setIsCrmValid(false);
        setCrmValidationError("");
    }, []);

    const onChangeUserTypes = (values) => {
        // values is an array of selected types
        const previousTypes = userTypes;

        // DISTRIBUTOR_RESPONSIBLE is exclusive - cannot be combined with any other type
        let finalValues = values || [];

        // If user just selected DISTRIBUTOR_RESPONSIBLE
        if (includesDistributorResponsible(finalValues)) {
            // Keep only DISTRIBUTOR_RESPONSIBLE, remove all others
            finalValues = [UserTypeEnum.DISTRIBUTOR_RESPONSIBLE.value];
        }

        setUserTypes(finalValues);

        // Reset institutions when types change
        setSelectedInstitutions([]);

        // Reset CRM fields if prescriber is removed
        if (includesPrescriber(previousTypes) && !includesPrescriber(values)) {
            setCrm("");
            setCrmState("");
            resetCrmValidation();
        }

        // Reset CRF fields if dispenser is removed
        if (includesDispenser(previousTypes) && !includesDispenser(values)) {
            setCrf("");
            setCrfState("");
        }

        // Reset responsible for institutions if institution_responsible or distributor_responsible is removed
        const hadResponsibleType = includesInstitutionResponsible(previousTypes) || includesDistributorResponsible(previousTypes);
        const hasResponsibleType = includesInstitutionResponsible(finalValues) || includesDistributorResponsible(finalValues);
        if (hadResponsibleType && !hasResponsibleType) {
            setResponsibleForInstitutions([]);
        }
    }

    const onChangeName = (value) => {
        setName(value);
        resetCrmValidation();
    }

    const onChangeCrm = (value) => {
        setCrm(value);
        resetCrmValidation();
    }

    const onChangeCrmState = (value) => {
        setCrmState(value);
        resetCrmValidation();
    }

    const handleInstitutionsLoaded = useCallback((institutions) => {
        // Populate institutionNamesMap with loaded institution data
        const namesMap = {};
        institutions.forEach(inst => {
            // Only add if label is not empty (preserve manually created institutions)
            if (inst.label && inst.label.trim() !== '') {
                namesMap[inst.value] = inst.label;
            }
        });
        setInstitutionNamesMap(prev => ({
            ...prev,
            ...namesMap
        }));
    }, []);

    const handleInstitutionSave = useCallback(async (payload) => {
        try {
            const response = await fetchData("/institution/public", "POST", false, payload);
            if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
                const newInst = response.data.data;
                setCreatedInstitutions(prev => [...prev, newInst]);
                // Add new institution to the selected institutions array
                setSelectedInstitutions(prev => {
                    // Remove "NO-INSTITUTION" if present and add the new institution
                    const filtered = prev.filter(id => id !== "NO-INSTITUTION");
                    return [...filtered, newInst.organization_branch_pk];
                });

                // Add institution name to map
                setInstitutionNamesMap(prev => ({
                    ...prev,
                    [newInst.organization_branch_pk]: newInst.name
                }));

                // Auto-add to "Responsible For" if user type is INSTITUTION_RESPONSIBLE or DISTRIBUTOR_RESPONSIBLE
                if (includesInstitutionResponsible(userTypes) || includesDistributorResponsible(userTypes)) {
                    setResponsibleForInstitutions(prev => [...prev, newInst.organization_branch_pk]);
                }

                setInstitutionModalVisible(false);
                return true;
            } else {
                console.error("Failed to create institution:", response.data?.detail);
                const errorMsg = response.data?.detail || intl.get("ErrorCreatingInstitution").d("Erro ao criar instituição. Tente novamente.");
                Swal.fire({
                    icon: "error",
                    title: errorMsg,
                });
                return false;
            }
        } catch (error) {
            console.error("Error creating institution:", error);

            let errorMessage;

            if (error.response?.status === 409) {
                // CNPJ already exists
                errorMessage = intl.get("CnpjAlreadyExists").d("Já existe uma instituição com este CNPJ. Selecione-a na lista.");
            } else if (error.response?.data?.detail) {
                // Show backend error detail
                errorMessage = error.response.data.detail;
            } else if (error.message) {
                // Show generic error message
                errorMessage = intl.get("ErrorCreatingInstitution").d("Erro ao criar instituição") + ": " + error.message;
            } else {
                // Fallback error message
                errorMessage = intl.get("ErrorCreatingInstitution").d("Erro ao criar instituição. Tente novamente.");
            }

            Swal.fire({
                    icon: "error",
                    title: errorMessage,
                });
            return false;
        }
    }, [userTypes]);

    const handleSubmit = async () => {
        // Show errors on first submit attempt
        setShowErrors(true);

        if (!isFormValid || isSubmitting) return;

        setIsSubmitting(true);
        try {
            // Build roles based on selected types
            const roles = [];
            if (includesPrescriber(userTypes)) {
                roles.push(UserRole.DOCTOR);
            }
            if (includesDispenser(userTypes)) {
                roles.push(UserRole.PHARMACIST);
            }
            if (userTypes.includes(UserTypeEnum.INSTITUTION_RESPONSIBLE.value)) {
                roles.push(UserRole.INSTITUTION_RESPONSIBLE);
            }
            if (userTypes.includes(UserTypeEnum.DISTRIBUTOR_RESPONSIBLE.value)) {
                roles.push(UserRole.DISTRIBUTOR_RESPONSIBLE);
            }

            // Base dms_user structure
            const dmsUser = {
                user_login: email,
                user_email: email,
                user_name: name,
                user_phone: phone.replace(/\D/g, ''),
                user_password: password,
                roles: roles,
            };

            // Generate unique address name to avoid duplicates
            const randomSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
            const uniqueAddressName = `${name} - ${randomSuffix}`;

            // Base person structure
            const personData = {
                cpf: cpf.replace(/\D/g, ''),
                dms_user: dmsUser,
            };

            // Only add address if at least one address field is filled (required for non-dispensers, optional for dispensers)
            if (zipCode.trim() !== "" || address.trim() !== "" || addressNumber.trim() !== "" || city.trim() !== "" || addressState !== "") {
                personData.address = {
                    address_name: uniqueAddressName,
                    number: addressNumber,
                    address: address,
                    zip_code: zipCode.replace(/\D/g, ''),
                    city: city,
                    state: addressState,
                    country_code: "BR",
                };
            }

            // For multi-type registration, use unified endpoint
            // that creates person with multiple types at once
            let endpoint = "/person/public/multi-type";
            let payload = {
                person: personData,
                user_types: userTypes,
            };

            // Add institution links if selected (skip "NO-INSTITUTION")
            const validInstitutions = selectedInstitutions.filter(id => id !== "NO-INSTITUTION");
            if (validInstitutions.length > 0) {
                payload.organization_branch_pks = validInstitutions;
            }

            // Add created institutions for auto-approval
            if (createdInstitutions.length > 0) {
                payload.created_institution_pks = createdInstitutions.map(inst => inst.organization_branch_pk);
            }

            // Add responsible_for field for institution responsibles and distributor responsibles
            if ((includesInstitutionResponsible(userTypes) || includesDistributorResponsible(userTypes)) && responsibleForInstitutions.length > 0) {
                payload.responsible_for_institution_pks = responsibleForInstitutions;
            }

            // Add type-specific data
            if (includesPrescriber(userTypes)) {
                payload.doctor_data = {
                    crm: crm,
                    crm_state: crmState,
                    institution_link_declaration: institutionLinkDeclaration,
                };
            }

            if (includesDispenser(userTypes)) {
                payload.pharmacist_data = {
                    crf: crf,
                    crf_state: crfState,
                };
            }

            const response = await fetchData(endpoint, "POST", false, payload);

            if (response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
                setCreatedUserName(name);
                setSuccessModalOpen(true);
                startCountdown();
            } else {
                setStatus("error");
                setMessage(response.data?.detail || intl.get("ErrorCreatingAccount").d("Error creating account"));
            }
        } catch (error) {
            setStatus("error");
            if (error.response?.data?.detail) {
                setMessage(error.response.data.detail);
            } else {
                setMessage(intl.get("ErrorCreatingAccount").d("Error creating account"));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        state: { status, message, password, confirmPassword, cpfIsValid, selectedInstitutions, responsibleForInstitutions, institutionNamesMap, showResponsibleForField, userTypes, email, name, phone, crm, crmState, crf, crfState, crmValidationWarning, cpf, isSubmitting, zipCode, city, addressState, address, addressNumber, legalDocumentsAccepted, institutionLinkDeclaration, legalDocument, documentDialogOpen, loadingDocument, successModalOpen, countdown, createdUserName, isFormValid, validationErrors, fieldErrors, isValidatingCrm, isCrmValid, crmValidationError, canValidateCrm, institutionModalVisible, createdInstitutions },
        handlers: { setStatus, setMessage, setPassword, setConfirmPassword, setCpfIsValid, setSelectedInstitutions, setResponsibleForInstitutions, setUserTypes, setEmail, setName, setPhone, setCrm, setCrmState, setCrf, setCrfState, setCpf, setIsSubmitting, setZipCode, setCity, setAddressState, setAddress, setAddressNumber, setLegalDocumentsAccepted, setInstitutionLinkDeclaration, setDocumentDialogOpen, setLoadingDocument, setSuccessModalOpen, setCountdown, setCreatedUserName, handleOpenDocument, handleCloseDocument, startCountdown, handleSuccessModalClose, onChangeUserTypes, onChangeName, onChangeCrm, onChangeCrmState, handleCrmValidation, handleSubmit, setInstitutionModalVisible, handleInstitutionSave, handleInstitutionsLoaded }
    };
}
